import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {Order} from "../models/shop/order.model";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {OrdersDgSearchResults} from "../models/datagrid-search-results/orders.dg-search-results.model";
import {Snapshot} from "../models/snpashot.model";
import {environment} from "../../environments/environment";
import {PaymentMethodP24} from "../models/finance/payment-method.model";
import {Invoice} from "../models/invoice.model";
import {CsvReportField} from "../models/csv-report-field.interface";

export class PaymentStatus {
  status: number;
  constructor(status: number) {
    this.status = status;
  }
}

export class ErrorMsg {
  public main: number;
  constructor(main: number) {
    this.main = main;
  }
}

/**
 * Główny serwis zarządzający transakcjami (Zamówieniami) w systemie.
 * Obsługuje cykl życia zamówienia, integrację z bramką płatności (P24),
 * procesy fakturowania (w tym korekty) oraz operacje ratunkowe dla błędnych transakcji.
 */
@Injectable({
  providedIn: 'root'
})
export class OrderService implements DataGridServiceInterface<Observable<OrdersDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę zamówień do wyświetlenia w tabeli DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<OrdersDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return new OrdersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Order(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje zamówienia na podstawie zaawansowanych filtrów (np. ID zamówienia, e-mail klienta, status płatności).
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<OrdersDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new OrdersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Order(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza asynchroniczną weryfikację statusu płatności bezpośrednio w bramce Przelewy24.
   *
   * @param uuid Identyfikator zamówienia.
   * @returns Observable emitujący obiekt ze zaktualizowanym kodem statusu.
   */
  verifyPaymentStatus(uuid: string): Observable<PaymentStatus> {
    return this.apiService.post<VetineoApiResponse>(`/payment/p24/${uuid}/verify-payment-status`, {})
      .pipe(take(1), map(vetineoApiResponse => {
        if (vetineoApiResponse.ok) {
          console.log(vetineoApiResponse.response);
          return this.commonUtils.bestTypedCopyEver<PaymentStatus>(new PaymentStatus(0), vetineoApiResponse.response);
        } else {
          let msg = this.commonUtils.bestTypedCopyEver<ErrorMsg>(new ErrorMsg(0), vetineoApiResponse.errors);
          return new PaymentStatus(msg.main);
        }
      }));
  }

  /**
   * Pobiera historię zmian audytowych (Snapshot) dla konkretnego zamówienia.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Ręcznie zmienia status zamówienia.
   */
  changeStatus(uuid: string, orderStatus: number): Observable<Order> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/orders/status', {
      'uuid': uuid,
      'status': orderStatus.toString()
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła do klienta bezpośredni link e-mail/SMS umożliwiający opłacenie zamówienia.
   */
  sendOrderPaymentLink(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-payment-link/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces generowania faktury sprzedażowej dla danego zamówienia.
   */
  createInvoice(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/create-invoice/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera obiekt wygenerowanej wcześniej faktury.
   */
  checkInvoice(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/invoice-check/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Invoice>(new Invoice(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Generuje fakturę korygującą (Corrective Invoice) w przypadku zwrotu całości lub części zamówienia.
   */
  createCorrectiveInvoice(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/create-corrective-invoice/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Otwiera nowe okno przeglądarki wywołujące bezpośredni endpoint backendu,
   * który zwraca wygenerowany plik PDF z fakturą korygującą.
   */
  printCorrectiveInvoice(refundRequestUUID: string, orderUUID: string) {
    window.open(`${environment.VETINEO_API_WS_URL}/dashboard/orders/print-corrective-invoice/${refundRequestUUID}/${orderUUID}`, '_blank');
  }

  /**
   * Wysyła fakturę bezpośrednio do klienta docelowego.
   */
  sendInvoice(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-invoice/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła fakturę na wewnętrzny adres e-mail kliniki / księgowości.
   */
  sendInvoiceToOffice(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-invoice/office/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Nawiguje użytkownika z widoku zamówienia bezpośrednio do powiązanej z nim rezerwacji medycznej.
   */
  goToVetReservation(vetReservationUUID: string) {
    window.open(`/vet-reservation?uuid=${vetReservationUUID}`, '_blank');
  }

  /**
   * Otwiera nowe okno przeglądarki pobierające PDF standardowej faktury sprzedażowej.
   */
  printInvoice(userUUID: string, orderUUID: string) {
    window.open(`${environment.VETINEO_API_WS_URL}/dashboard/orders/print-invoice/${userUUID}/${orderUUID}`, '_blank');
  }

  /**
   * Zmienia flagę zamówienia oznaczającą, że fizyczny paragon fiskalny został wydrukowany i wydany.
   */
  confirmThatReceiptWasIssued(orderUUID: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/confirm-receipt-issued/${orderUUID}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła powiadomienie "przypominające" o nieopłaconym zamówieniu.
   */
  sendOrderPaymentReminderLink(uuid: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-payment-reminder-link/${uuid}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły pojedynczego zamówienia.
   */
  getByUUID(orderUUID: string): Observable<Order> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/${orderUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wysyła fakturę korygującą do księgowości kliniki.
   */
  sendCorrectiveInvoiceToOffice(orderUUID: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-corrective-invoice/office/${orderUUID}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Wysyła fakturę korygującą bezpośrednio do klienta docelowego.
   */
  sendCorrectiveInvoiceToClient(orderUUID: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/send-corrective-invoice/${orderUUID}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Inicjuje asynchroniczne generowanie szczegółowego raportu finansowego (CSV) dla wybranego zakresu zamówień.
   */
  sendCSVReportRequest(requestObj: {dateFrom: string; dateTo: string; vetcodeId: number; csvReportFields: CsvReportField[]; skipRefunds: boolean}) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/orders/csv-report', requestObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera listę metod płatności udostępnianych przez bramkę (P24) do renderingu widoków kontrolnych.
   */
  getAvailablePaymentMethods(): Observable<PaymentMethodP24[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/payment/p24/payment-methods`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.list) {
        return this.commonUtils.bestTypedArrayCopyEver<PaymentMethodP24, PaymentMethodP24[]>(new PaymentMethodP24(), [], vetineoApiResponse.response.list);
      }
    }));
  }

  /**
   * Wymusza zmianę flagi kompletności zamówienia.
   */
  markOrderAsCompleted(requestObj: {completed: boolean; orderUUID: string}) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/set-cmpl/${requestObj.completed}/${requestObj.orderUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera plik PDF ze złączonymi fakturami dla przekazanej listy zamówień (order UUID).
   * userUUID jest ustalany po stronie backendu.
   */
  printMergedInvoices(orderUuids: string[]): Observable<Blob> {
    return this.apiService.post<Blob>(`/dashboard/orders/print-merged-invoices`, {orderUUIDs: orderUuids}, {responseType: 'blob'});
  }

  /**
   * Zapisuje potwierdzenie wydruku faktur dla przekazanej listy zamówień (order UUID).
   * Zwraca listę UUID zamówień, dla których status został zaktualizowany.
   */
  saveInvoicePrintStatus(orderUuids: string[]) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/orders/save-invoice-print-status`, orderUuids).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Funkcja ratunkowa (Troubleshooting): Próbuje "naprawić" zamówienie, które
   * zacięło się w stanie błędu podczas komunikacji z operatorem płatności.
   */
  repairOrderWithErrorDuringPaymentProcessing(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/repair/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Funkcja ratunkowa (Troubleshooting): Próbuje naprawić zablokowane zamówienie
   * i wymusza ponowne wygenerowanie/dosłanie faktury.
   */
  repairOrderAndSendInvoice(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/orders/repair-invoice/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera szczegóły zamówienia na podstawie uniwersalnego parametru.
   */
  getOrder(param: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/order-details/${param}`).pipe(take(1), map(vetineoApiResponse => {
    if (vetineoApiResponse.ok) {
      console.log(vetineoApiResponse.response)
      return this.commonUtils.bestTypedCopyEver<Order>(new Order(), vetineoApiResponse.response);
    } else {
      return undefined;
    }
  }));
  }
}
